import { type ReactNode, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { i18n, isSideState, isTerminal, nextStates, type PalletState } from '@wms/shared';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth-context.js';
import { formatCents, formatDate, formatQty } from '../lib/format.js';
import { canEditPalletPhotos } from '../lib/pallet-actions.js';
import { UnpackPalletDialog } from '../components/UnpackPalletDialog.js';
import { PalletTopupDialog } from '../components/PalletTopupDialog.js';
import { MoveToShelfDialog } from '../components/MoveToShelfDialog.js';
import { PhotoGallery } from '../components/PhotoGallery.js';
import { PriceOverrideDialog } from '../components/PriceOverrideDialog.js';
import { TransitionDialog } from '../components/TransitionDialog.js';
import { RepackDialog } from '../components/RepackDialog.js';

const { t } = i18n;

interface PalletStockMovement {
  id: string;
  type: string;
  qty: string | number;
  skuId: string;
  timestamp: string;
  comment: string | null;
  sku: { code: string; nameLt: string; nameEn: string | null; partnerId: string };
}

interface PalletLink {
  id: string;
  code: string;
  state: PalletState;
}

interface LocationChange {
  id: string;
  fromRow: string | null;
  fromShelf: string | null;
  fromPosition: string | null;
  toRow: string | null;
  toShelf: string | null;
  toPosition: string | null;
  reason: string | null;
  timestamp: string;
}

interface PalletDetail {
  id: string;
  code: string;
  state: PalletState;
  expectedAt: string | null;
  arrivedAt: string | null;
  dispatchedAt: string | null;
  contentsVisibleAt: string | null;
  depletedAt: string | null;
  confirmedBy: { displayName: string; email: string } | null;
  depletionChangeId: string | null;
  comment: string | null;
  sourceOrder: { id: string; code: string; status: string } | null;
  count: number;
  locationRow: string | null;
  locationShelf: string | null;
  locationPosition: string | null;
  parentPalletId: string | null;
  customer: { id: string; code: string; name: string } | null;
  ownerPartner: { id: string; code: string; name: string } | null;
  destinationPartner: { id: string; code: string; name: string } | null;
  parent: PalletLink | null;
  children: PalletLink[];
  contents: {
    id: string;
    skuId: string;
    originalQty: string;
    remainingQty: string;
    sku: { id: string; code: string; nameLt: string; nameEn: string | null; type: string };
  }[];
  contentMovementsFrom: {
    id: string;
    type: string;
    qty: string | number;
    timestamp: string;
    reason: string | null;
    sku: { code: string; nameLt: string };
    targetPallet: { id: string; code: string } | null;
    sourceOrder: { id: string; code: string; status: string } | null;
  }[];
  contentMovementsTo: {
    id: string;
    type: string;
    qty: string | number;
    timestamp: string;
    reason: string | null;
    sku: { code: string; nameLt: string };
    sourcePallet: { id: string; code: string } | null;
  }[];
  lineageAsTarget: { createdAt: string; sourcePallet: PalletLink }[];
  lineageAsSource: { createdAt: string; targetPallet: PalletLink }[];
  stockMovements: PalletStockMovement[];
  orderLinks?: { order: { status: string } }[];
  changes: {
    id: string;
    fromState: PalletState | null;
    toState: PalletState;
    timestamp: string;
    reason: string | null;
  }[];
  locationChanges: LocationChange[];
  overrides: {
    id: string;
    periodFrom: string;
    periodTo: string;
    mode: 'FLAT_RATE' | 'FLAT_AMOUNT';
    valueCents: number;
    reason: string;
  }[];
}

function formatChangeCoords(
  row: string | null,
  shelf: string | null,
  position: string | null,
): string {
  const parts = [row, shelf, position].filter((x): x is string => Boolean(x && x.length > 0));
  return parts.length === 0 ? '—' : parts.join('-');
}

export function PalletDetailPage() {
  const { user } = useAuth();
  const { id = '' } = useParams();
  const [pallet, setPallet] = useState<PalletDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [busy, setBusy] = useState<PalletState | null>(null);
  const [unpackOpen, setUnpackOpen] = useState(false);
  const [topupOpen, setTopupOpen] = useState(false);
  const [moveToShelfOpen, setMoveToShelfOpen] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [pendingTransition, setPendingTransition] = useState<PalletState | null>(null);
  const [repackOpen, setRepackOpen] = useState(false);
  const [locationEdit, setLocationEdit] = useState<{
    row: string;
    shelf: string;
    position: string;
  } | null>(null);

  async function saveLocation() {
    if (!pallet || !locationEdit) return;
    try {
      await api(`/pallets/${pallet.id}/location`, {
        method: 'PATCH',
        body: {
          locationRow: locationEdit.row.trim() || null,
          locationShelf: locationEdit.shelf.trim() || null,
          locationPosition: locationEdit.position.trim() || null,
        },
      });
      setLocationEdit(null);
      setReloadKey((k) => k + 1);
    } catch {
      window.alert(t('common.error'));
    }
  }

  useEffect(() => {
    setPallet(null);
    api<PalletDetail>(`/pallets/${id}`)
      .then(setPallet)
      .catch((err: unknown) => setError(String(err)));
  }, [id, reloadKey]);

  async function confirmTransition(toState: PalletState, reason: string, date: string) {
    if (!pallet) return;
    setBusy(toState);
    try {
      await api(`/pallets/${id}/transition`, {
        method: 'POST',
        body: { toState, reason, ...(date ? { date } : {}) },
      });
      setPendingTransition(null);
      setReloadKey((k) => k + 1);
    } catch {
      window.alert(t('common.error'));
    } finally {
      setBusy(null);
    }
  }

  if (error) return <div className="text-sm text-red-600">{error}</div>;
  if (!pallet) return <div className="text-sm text-muted-foreground">{t('common.loading')}</div>;

  const next = nextStates(pallet.state);
  const depletionConfirmer = pallet.confirmedBy?.displayName.trim() || pallet.confirmedBy?.email;

  const canUnpack =
    !pallet.contentsVisibleAt &&
    Boolean(pallet.ownerPartner ?? pallet.destinationPartner) &&
    (pallet.state === 'ARRIVED' ||
      pallet.state === 'PARTIALLY_DISPATCHED' ||
      pallet.state === 'DAMAGED');
  const canRepack = pallet.state === 'DAMAGED';
  const canMoveToShelf =
    Boolean(user?.roles.some((binding) => ['ADMIN', 'OPERATOR'].includes(binding.role))) &&
    Boolean(pallet.contentsVisibleAt) &&
    !pallet.depletedAt &&
    ['ARRIVED', 'PARTIALLY_DISPATCHED', 'DAMAGED'].includes(pallet.state) &&
    pallet.contents.some((line) => Number(line.remainingQty) > 0) &&
    !(pallet.orderLinks ?? []).some((link) =>
      [
        'DRAFT',
        'SUBMITTED',
        'ON_HOLD',
        'IN_ASSEMBLY',
        'ASSEMBLED',
        'PACKING_ON_PALLET',
        'READY_FOR_SHIPMENT',
      ].includes(link.order.status),
    );
  const canEditPhotos = canEditPalletPhotos(pallet.state);
  const awaitsDepletionConfirmation =
    !isTerminal(pallet.state) &&
    pallet.contents.length > 0 &&
    pallet.contents.every((line) => Number(line.remainingQty) <= 0);
  const currentPalletId = pallet.id;
  const depletionMovements = pallet.contentMovementsFrom.filter((movement) =>
    ['CONSUMED', 'DISPATCHED', 'LOST', 'TRANSFERRED', 'ADJUSTED'].includes(movement.type),
  );
  const fromShelfMovements =
    pallet.contentMovementsTo?.filter(
      (movement) => movement.type === 'TRANSFERRED' && !movement.sourcePallet,
    ) ?? [];

  async function confirmDepleted() {
    const reason = window.prompt(t('pallet.depleted.reason_prompt'))?.trim();
    if (!reason) return;
    try {
      await api(`/pallets/${currentPalletId}/confirm-depleted`, {
        method: 'POST',
        body: { reason },
      });
      setReloadKey((key) => key + 1);
    } catch {
      window.alert(t('common.error'));
    }
  }

  return (
    <section className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          {pallet.customer ? (
            <Link
              to={`/customers/${pallet.customer.id}`}
              className="text-xs text-muted-foreground hover:underline"
            >
              ← {pallet.customer.name}
            </Link>
          ) : pallet.ownerPartner ? (
            <span className="text-xs text-muted-foreground">
              {pallet.ownerPartner.code} — {pallet.ownerPartner.name}
            </span>
          ) : null}
          <h2 className="text-xl font-semibold mt-1 font-mono">{pallet.code}</h2>
          {pallet.depletedAt ? (
            <div className="mt-1 space-y-1">
              <span className="inline-flex rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-medium text-foreground">
                {t('pallet.depleted.badge')}
              </span>
              {depletionConfirmer ? (
                <div className="text-xs text-muted-foreground">
                  {t('pallet.depleted.confirmed_by')}: {depletionConfirmer}
                </div>
              ) : null}
            </div>
          ) : awaitsDepletionConfirmation ? (
            <span className="mt-1 inline-flex rounded-full border border-amber-400 bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
              {t('pallet.empty.badge')}
            </span>
          ) : null}
          {pallet.destinationPartner ? (
            <div className="mt-1 text-xs text-muted-foreground">
              {t('pallet.destination_partner')}:{' '}
              <span className="font-medium text-foreground">
                {pallet.destinationPartner.code} — {pallet.destinationPartner.name}
              </span>
            </div>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <a
            href={`/api/pallets/${pallet.id}/label.pdf?format=thermal`}
            target="_blank"
            rel="noreferrer"
            className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted"
          >
            {t('pallet.label.thermal')}
          </a>
          <a
            href={`/api/pallets/${pallet.id}/label.pdf?format=a4`}
            target="_blank"
            rel="noreferrer"
            className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted"
          >
            {t('pallet.label.a4')}
          </a>
          {user?.roles.some((r) => ['ADMIN', 'OPERATOR'].includes(r.role)) &&
          (pallet.ownerPartner ?? pallet.destinationPartner) &&
          !pallet.depletedAt &&
          ['ARRIVED', 'PARTIALLY_DISPATCHED', 'DAMAGED'].includes(pallet.state) &&
          !(pallet.orderLinks ?? []).length &&
          (pallet.contentsVisibleAt || !pallet.contents.length) ? (
            <button
              type="button"
              onClick={() => setTopupOpen(true)}
              className="rounded border border-border px-3 py-1.5 text-sm"
            >
              {t('pallet.topup.title')}
            </button>
          ) : null}
          {canUnpack ? (
            <button
              type="button"
              onClick={() => setUnpackOpen(true)}
              className="rounded bg-primary text-primary-foreground px-3 py-1.5 text-sm"
            >
              {t('pallet.unpack.action')}
            </button>
          ) : null}
          {canMoveToShelf ? (
            <button
              type="button"
              onClick={() => setMoveToShelfOpen(true)}
              className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted"
            >
              {t('pallet.shelf.action')}
            </button>
          ) : null}
          {canRepack ? (
            <button
              type="button"
              onClick={() => setRepackOpen(true)}
              className="rounded border border-amber-500 text-amber-900 px-3 py-1.5 text-sm hover:bg-amber-100 dark:text-amber-100 dark:hover:bg-amber-900/30"
            >
              {t('pallet.repack.action')}
            </button>
          ) : null}
          {awaitsDepletionConfirmation && !pallet.depletedAt ? (
            <button
              type="button"
              onClick={() => void confirmDepleted()}
              className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted"
            >
              {t('pallet.depleted.confirm')}
            </button>
          ) : null}
        </div>
      </div>

      {pallet.parent ||
      pallet.children.length > 0 ||
      pallet.lineageAsTarget.length > 0 ||
      pallet.lineageAsSource.length > 0 ? (
        <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
          {pallet.parent ? (
            <span>
              {t('pallet.repack.parent_link')}:{' '}
              <Link
                to={`/pallets/${pallet.parent.id}`}
                className="font-mono text-foreground hover:underline"
              >
                {pallet.parent.code}
              </Link>
            </span>
          ) : null}
          {pallet.children.map((child) => (
            <span key={child.id}>
              {t('pallet.repack.child_link')}:{' '}
              <Link
                to={`/pallets/${child.id}`}
                className="font-mono text-foreground hover:underline"
              >
                {child.code}
              </Link>
            </span>
          ))}
          {pallet.lineageAsTarget.map((link) => (
            <span key={`from:${link.sourcePallet.id}`}>
              {t('pallet.genealogy.source')}:{' '}
              <Link
                to={`/pallets/${link.sourcePallet.id}`}
                className="font-mono text-foreground hover:underline"
              >
                {link.sourcePallet.code}
              </Link>{' '}
              · {formatDate(link.createdAt)}
            </span>
          ))}
          {pallet.lineageAsSource.map((link) => (
            <span key={`to:${link.targetPallet.id}`}>
              {t('pallet.genealogy.target')}:{' '}
              <Link
                to={`/pallets/${link.targetPallet.id}`}
                className="font-mono text-foreground hover:underline"
              >
                {link.targetPallet.code}
              </Link>{' '}
              · {formatDate(link.createdAt)}
            </span>
          ))}
        </div>
      ) : null}

      <div className="grid gap-3 md:grid-cols-2 text-sm">
        <Info label={t('pallet.col.state')}>{t(`pallet.state.${pallet.state}`)}</Info>
        <Info label={t('pallet.col.count')}>{pallet.count}</Info>
        <Info label={t('pallet.col.expected')}>{formatDate(pallet.expectedAt)}</Info>
        <Info label={t('pallet.col.arrived')}>{formatDate(pallet.arrivedAt)}</Info>
        <Info label={t('pallet.col.dispatched')}>{formatDate(pallet.dispatchedAt)}</Info>
        <Info label={t('pallet.col.comment')}>
          {pallet.sourceOrder ? (
            <>
              {t('order.pack.source_order')}:{' '}
              <Link
                to={`/orders/${pallet.sourceOrder.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono hover:underline"
              >
                {pallet.sourceOrder.code}
              </Link>
            </>
          ) : (
            (pallet.comment ?? '—')
          )}
        </Info>
      </div>

      <div className="rounded border border-border p-3 text-sm">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              {t('location.label')}
            </div>
            <div className="font-mono">{formatLocation(pallet)}</div>
          </div>
          <button
            type="button"
            onClick={() =>
              setLocationEdit({
                row: pallet.locationRow ?? '',
                shelf: pallet.locationShelf ?? '',
                position: pallet.locationPosition ?? '',
              })
            }
            className="rounded border border-border px-2 py-1 text-xs hover:bg-muted"
          >
            {t('common.edit')}
          </button>
        </div>
        {pallet.locationChanges.length > 0 ? (
          <div className="mt-3 border-t border-border/60 pt-2 text-xs">
            <div className="mb-1 uppercase tracking-wide text-muted-foreground">
              {t('location.history_title')}
            </div>
            <ul className="space-y-0.5 text-muted-foreground">
              {pallet.locationChanges.map((c) => (
                <li key={c.id} className="font-mono">
                  {formatChangeCoords(c.fromRow, c.fromShelf, c.fromPosition)} →{' '}
                  {formatChangeCoords(c.toRow, c.toShelf, c.toPosition)}
                  <span className="ml-2 font-sans not-italic text-[11px]">
                    {formatDate(c.timestamp)}
                    {c.reason ? ` · ${c.reason}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {locationEdit ? (
          <div className="mt-3 grid grid-cols-4 gap-2">
            <input
              value={locationEdit.row}
              onChange={(e) => setLocationEdit({ ...locationEdit, row: e.target.value })}
              maxLength={8}
              placeholder={t('location.row')}
              className="rounded border border-border bg-background px-2 py-1.5 text-center font-mono"
            />
            <input
              value={locationEdit.shelf}
              onChange={(e) => setLocationEdit({ ...locationEdit, shelf: e.target.value })}
              maxLength={8}
              placeholder={t('location.shelf')}
              className="rounded border border-border bg-background px-2 py-1.5 text-center font-mono"
            />
            <input
              value={locationEdit.position}
              onChange={(e) => setLocationEdit({ ...locationEdit, position: e.target.value })}
              maxLength={8}
              placeholder={t('location.position')}
              className="rounded border border-border bg-background px-2 py-1.5 text-center font-mono"
            />
            <div className="flex gap-1 justify-end">
              <button
                type="button"
                onClick={() => setLocationEdit(null)}
                className="rounded border border-border px-2 py-1 text-xs"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={() => void saveLocation()}
                className="rounded bg-primary text-primary-foreground px-2 py-1 text-xs"
              >
                {t('common.save')}
              </button>
            </div>
          </div>
        ) : null}
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t('pallet.contents.title')}
          </h3>
          <span className="text-xs text-muted-foreground">
            {pallet.contentsVisibleAt
              ? `${t('pallet.contents.visible')} · ${formatDate(pallet.contentsVisibleAt)}`
              : t('pallet.contents.hidden')}
          </span>
        </div>
        {pallet.contents.length === 0 ? (
          <div className="rounded border border-dashed border-border p-3 text-sm text-muted-foreground">
            {t('pallet.contents.empty')}
          </div>
        ) : (
          <div className="overflow-x-auto rounded border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left text-xs">
                <tr>
                  <th className="px-3 py-2">SKU</th>
                  <th className="px-3 py-2">{t('pallet.contents.original')}</th>
                  <th className="px-3 py-2">{t('pallet.contents.remaining')}</th>
                  <th className="px-3 py-2">{t('pallet.contents.location')}</th>
                </tr>
              </thead>
              <tbody>
                {pallet.contents.map((line) => (
                  <tr key={line.id} className="border-t border-border">
                    <td className="px-3 py-2">
                      <Link
                        to={`/partners/${pallet.ownerPartner?.id ?? pallet.destinationPartner?.id}/skus/${line.skuId}`}
                        className="hover:underline"
                      >
                        <span className="font-mono text-xs">{line.sku.code}</span>{' '}
                        <span className="text-muted-foreground">{line.sku.nameLt}</span>
                      </Link>
                    </td>
                    <td className="px-3 py-2 tabular-nums">{formatQty(line.originalQty)}</td>
                    <td className="px-3 py-2 tabular-nums font-medium">
                      {formatQty(line.remainingQty)}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{pallet.code}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {fromShelfMovements.length > 0 ? (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t('pallet.shelf.inbound_history')}
          </h3>
          <ul className="space-y-2">
            {fromShelfMovements.map((movement) => (
              <li key={movement.id} className="rounded border border-border px-3 py-2 text-sm">
                <div className="flex justify-between gap-2">
                  <span>
                    {formatQty(movement.qty)} ×{' '}
                    <span className="font-mono">{movement.sku.code}</span> {movement.sku.nameLt}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {formatDate(movement.timestamp)}
                  </span>
                </div>
                {movement.reason ? (
                  <p className="mt-1 text-xs text-muted-foreground">{movement.reason}</p>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {depletionMovements.length > 0 ? (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t('pallet.depletion_history.title')}
          </h3>
          <ul className="space-y-2">
            {depletionMovements.map((movement) => (
              <li key={movement.id} className="rounded border border-border px-3 py-2 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium">
                    {movement.type === 'TRANSFERRED' && !movement.targetPallet
                      ? t('pallet.depletion_history.to_shelf')
                      : t(`pallet.depletion_history.method.${movement.type}`)}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {formatDate(movement.timestamp)}
                  </span>
                </div>
                <div className="mt-1 text-xs">
                  <span className="tabular-nums">{formatQty(movement.qty)}</span> ×{' '}
                  <span className="font-mono">{movement.sku.code}</span>{' '}
                  <span className="text-muted-foreground">{movement.sku.nameLt}</span>
                </div>
                {movement.sourceOrder ? (
                  <div className="mt-1 text-xs text-muted-foreground">
                    {t('pallet.depletion_history.order')}:{' '}
                    <Link
                      to={`/orders/${movement.sourceOrder.id}`}
                      className="font-mono text-foreground hover:underline"
                    >
                      {movement.sourceOrder.code}
                    </Link>
                  </div>
                ) : movement.targetPallet ? (
                  <div className="mt-1 text-xs text-muted-foreground">
                    {t('pallet.depletion_history.target_pallet')}:{' '}
                    <Link
                      to={`/pallets/${movement.targetPallet.id}`}
                      className="font-mono text-foreground hover:underline"
                    >
                      {movement.targetPallet.code}
                    </Link>
                  </div>
                ) : null}
                {movement.reason && !movement.sourceOrder ? (
                  <div className="mt-1 text-xs text-muted-foreground">
                    {t('pallet.depletion_history.reason')}: {movement.reason}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {next.length > 0 ? (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t('pallet.transition.title')}
          </h3>
          <div className="flex flex-wrap gap-2">
            {next
              // REPACKED + UNPACKED_TO_STOCK have dedicated dialogs (Repack / Unpack)
              // up top — drop them from the generic transition row so we don't
              // show two ways to do the same thing.
              .filter((s) => s !== 'REPACKED' && s !== 'UNPACKED_TO_STOCK')
              .map((toState) => (
                <button
                  key={toState}
                  type="button"
                  onClick={() => setPendingTransition(toState)}
                  disabled={busy === toState}
                  className={`rounded border px-3 py-1.5 text-sm disabled:opacity-50 ${
                    isSideState(toState) || toState === 'CANCELLED'
                      ? 'border-amber-500 text-amber-900 hover:bg-amber-100 dark:text-amber-100 dark:hover:bg-amber-900/30'
                      : 'border-border hover:bg-muted'
                  }`}
                >
                  {busy === toState ? '…' : `→ ${t(`pallet.state.${toState}`)}`}
                </button>
              ))}
          </div>
        </div>
      ) : (
        <div className="text-sm text-muted-foreground">{t('pallet.transition.terminal')}</div>
      )}

      <PhotoGallery
        basePath={`/pallets/${pallet.id}/photos`}
        canEdit={canEditPhotos}
        reloadKey={reloadKey}
      />

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t('pallet.override.section')}
          </h3>
          <button
            type="button"
            onClick={() => setOverrideOpen(true)}
            className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted"
          >
            {t('pallet.override.add')}
          </button>
        </div>
        {pallet.overrides.length === 0 ? (
          <div className="text-xs text-muted-foreground">{t('pallet.override.empty')}</div>
        ) : (
          <ul className="space-y-1 text-sm">
            {pallet.overrides.map((o) => (
              <li
                key={o.id}
                className="flex items-center justify-between rounded border border-border px-3 py-2"
              >
                <div className="text-xs">
                  <span className="text-muted-foreground">
                    {formatDate(o.periodFrom)} → {formatDate(o.periodTo)}
                  </span>
                  <span className="mx-2">·</span>
                  <span>
                    {t(`pallet.override.mode.${o.mode}`)}: {formatCents(o.valueCents)}
                    {o.mode === 'FLAT_RATE' ? ` / ${t('pallet.override.day')}` : ''}
                  </span>
                  <div className="text-muted-foreground">{o.reason}</div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (!window.confirm(t('common.confirm_delete'))) return;
                    void (async () => {
                      try {
                        await api(`/pallets/${pallet.id}/price-overrides/${o.id}`, {
                          method: 'DELETE',
                        });
                        setReloadKey((k) => k + 1);
                      } catch {
                        window.alert(t('common.error'));
                      }
                    })();
                  }}
                  className="rounded border border-border px-2 py-1 text-xs"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {t('pallet.history.title')}
        </h3>
        {pallet.changes.length === 0 &&
        !pallet.contentMovementsFrom.some((movement) => movement.type === 'MADE_VISIBLE') ? (
          <div className="text-xs text-muted-foreground">{t('pallet.history.empty')}</div>
        ) : (
          <ul className="space-y-1 text-sm">
            {pallet.changes.map((c) => {
              const showMovements = c.toState === 'UNPACKED_TO_STOCK';
              return (
                <li key={c.id} className="rounded border border-border px-3 py-2">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs">
                      {c.fromState ? `${c.fromState} → ` : ''}
                      {c.toState}
                    </span>
                    <span className="text-xs text-muted-foreground">{formatDate(c.timestamp)}</span>
                  </div>
                  {c.reason ? (
                    <div className="mt-1 text-xs text-muted-foreground">{c.reason}</div>
                  ) : null}
                  {c.id === pallet.depletionChangeId && depletionConfirmer ? (
                    <div className="mt-1 text-xs text-muted-foreground">
                      {t('pallet.depleted.confirmed_by')}: {depletionConfirmer}
                    </div>
                  ) : null}
                  {showMovements && pallet.stockMovements.length > 0 ? (
                    <div className="mt-2 border-t border-border/60 pt-2">
                      <div className="mb-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                        {t('pallet.unpack.movements_title')}
                      </div>
                      <ul className="space-y-0.5 text-xs">
                        {pallet.stockMovements
                          .filter((m) => m.type === 'INBOUND_PALLET')
                          .map((m) => (
                            <li key={m.id}>
                              <Link
                                to={`/partners/${m.sku.partnerId}/skus/${m.skuId}`}
                                className="hover:underline"
                              >
                                <span className="tabular-nums">{formatQty(m.qty)}</span> ×{' '}
                                <span className="font-mono">{m.sku.code}</span>{' '}
                                <span className="text-muted-foreground">{m.sku.nameLt}</span>
                              </Link>
                            </li>
                          ))}
                      </ul>
                    </div>
                  ) : null}
                </li>
              );
            })}
            {pallet.contentMovementsFrom
              .filter((movement) => movement.type === 'MADE_VISIBLE')
              .map((movement) => (
                <li key={movement.id} className="rounded border border-border px-3 py-2">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs">
                      {t('pallet.history.contents_made_visible')}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {formatDate(movement.timestamp)}
                    </span>
                  </div>
                  {movement.reason ? (
                    <div className="mt-1 text-xs text-muted-foreground">{movement.reason}</div>
                  ) : null}
                  <div className="mt-2 border-t border-border/60 pt-2 text-xs">
                    <span className="tabular-nums">{formatQty(movement.qty)}</span> ×{' '}
                    <span className="font-mono">{movement.sku.code}</span>{' '}
                    <span className="text-muted-foreground">{movement.sku.nameLt}</span>
                  </div>
                </li>
              ))}
          </ul>
        )}
      </div>

      {topupOpen ? (
        <PalletTopupDialog
          palletId={pallet.id}
          onClose={() => setTopupOpen(false)}
          onSaved={() => setReloadKey((k) => k + 1)}
        />
      ) : null}
      {unpackOpen ? (
        <UnpackPalletDialog
          palletId={pallet.id}
          lockedPartner={pallet.ownerPartner ?? pallet.destinationPartner ?? null}
          registeredLines={pallet.contents.map((line) => ({
            skuId: line.skuId,
            qty: Number(line.originalQty),
          }))}
          onClose={() => setUnpackOpen(false)}
          onUnpacked={() => {
            setUnpackOpen(false);
            setReloadKey((k) => k + 1);
          }}
        />
      ) : null}
      {moveToShelfOpen ? (
        <MoveToShelfDialog
          palletId={pallet.id}
          contents={pallet.contents}
          onClose={() => setMoveToShelfOpen(false)}
          onMoved={() => {
            setMoveToShelfOpen(false);
            setReloadKey((key) => key + 1);
          }}
        />
      ) : null}
      {overrideOpen ? (
        <PriceOverrideDialog
          palletId={pallet.id}
          onClose={() => setOverrideOpen(false)}
          onSaved={() => {
            setOverrideOpen(false);
            setReloadKey((k) => k + 1);
          }}
        />
      ) : null}
      {pendingTransition ? (
        <TransitionDialog
          toState={pendingTransition}
          busy={busy === pendingTransition}
          onClose={() => setPendingTransition(null)}
          onConfirm={(reason, date) => confirmTransition(pendingTransition, reason, date)}
        />
      ) : null}
      {repackOpen ? (
        <RepackDialog
          palletId={pallet.id}
          onClose={() => setRepackOpen(false)}
          onRepacked={() => {
            setRepackOpen(false);
            setReloadKey((k) => k + 1);
          }}
        />
      ) : null}
    </section>
  );
}

function Info({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div>{children}</div>
    </div>
  );
}

function formatLocation(p: {
  locationRow: string | null;
  locationShelf: string | null;
  locationPosition: string | null;
}): string {
  const parts = [p.locationRow, p.locationShelf, p.locationPosition].filter((x): x is string =>
    Boolean(x && x.length > 0),
  );
  return parts.length === 0 ? '—' : parts.join('-');
}
